"use client";

import React, { createContext, useContext, useState, ReactNode } from "react";

type SectionsContextType = {
  sectionsToRender: Set<string>;
  preloadSectionsUntil: (targetId: string) => void;
  shouldRenderSection: (sectionId: string | undefined) => boolean;
};

const defaultContext: SectionsContextType = {
  sectionsToRender: new Set<string>(),
  preloadSectionsUntil: () => {},
  shouldRenderSection: () => false,
};

const SectionsContext = createContext<SectionsContextType>(defaultContext);

type SectionsProviderProps = {
  children: ReactNode;
  navigationItems: Array<{ href: string; label: string }>;
};

export function SectionsProvider({ children, navigationItems }: SectionsProviderProps) {
  const [sectionsToRender, setSectionsToRender] = useState<Set<string>>(new Set());

  const preloadSectionsUntil = (targetId: string) => {
    const targetIndex = navigationItems.findIndex((item) => item.href.substring(1) === targetId);

    if (targetIndex === -1) return;

    const newSectionsToRender = new Set<string>();

    navigationItems.forEach((item, index) => {
      if (index <= targetIndex) {
        const sectionId = item.href.substring(1);
        newSectionsToRender.add(sectionId);
      }
    });

    setSectionsToRender(newSectionsToRender);
  };

  const shouldRenderSection = (sectionId: string | undefined): boolean => {
    if (!sectionId) return true;
    return sectionsToRender.has(sectionId);
  };

  return (
    <SectionsContext.Provider
      value={{
        sectionsToRender,
        preloadSectionsUntil,
        shouldRenderSection,
      }}
    >
      {children}
    </SectionsContext.Provider>
  );
}

export const useSections = () => useContext(SectionsContext);
